import type { SearchQueryBody } from "@/types";

import type { EventQueryScope } from "../EventQueryScope";

export type EventsRequest = Partial<{
  datasetIds: string[];
  fileIds: string[];
  topicIds: string[];
  messagePathIds: string[];
  sessionIds: string[];
}>;

/**
 * Query key factory for event queries. Every method takes the current
 * `orgId` as its first argument so that every cache slot is implicitly
 * tenant-scoped — switching organizations cannot leak a previous org's
 * cached entries.
 */
export const eventQueryKeys = {
  all: (orgId: string) => ["events", orgId] as const,
  orgTags: (orgId: string) => [...eventQueryKeys.all(orgId), "tags"] as const,
  orgTagSearch: (orgId: string, search: string) =>
    [...eventQueryKeys.orgTags(orgId), "search", search] as const,
  lists: (orgId: string) => [...eventQueryKeys.all(orgId), "list"] as const,
  listsScope: (orgId: string, scope: EventQueryScope) =>
    scope.type === "org"
      ? ([...eventQueryKeys.lists(orgId), "org"] as const)
      : ([...eventQueryKeys.lists(orgId), scope.type, scope.id] as const),
  list: (orgId: string, scope: EventQueryScope, query: SearchQueryBody) =>
    [...eventQueryKeys.listsScope(orgId, scope), query] as const,
  details: (orgId: string) => [...eventQueryKeys.all(orgId), "detail"] as const,
  detail: (orgId: string, id: string) =>
    [...eventQueryKeys.details(orgId), id] as const,
  forWorkspaces: (orgId: string) =>
    [...eventQueryKeys.all(orgId), "workspace"] as const,
  forWorkspace: (orgId: string, request: EventsRequest) =>
    [
      ...eventQueryKeys.forWorkspaces(orgId),
      JSON.stringify({
        ...(request.datasetIds && { datasets: [...request.datasetIds].sort() }),
        ...(request.fileIds && { files: [...request.fileIds].sort() }),
        ...(request.topicIds && { topics: [...request.topicIds].sort() }),
        ...(request.messagePathIds && {
          messagePaths: [...request.messagePathIds].sort(),
        }),
        ...(request.sessionIds && {
          sessions: [...request.sessionIds].sort(),
        }),
      }),
    ] as const,
};
